import { ChevronDownIcon, ChevronUpIcon, ICON_SELECTOR } from '@ardoq/icons';
import { colors, r4 } from '@ardoq/design-tokens';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { TextInput, TextInputProps } from './TextInput';
import styled, { css } from 'styled-components';
import { isFunction } from 'lodash';

export type NumberInputProps = Omit<
  TextInputProps,
  'onValueChange' | 'value' | 'defaultValue' | 'rightIconName' | 'onChange'
> & {
  allowDecimals?: boolean;
  allowBlank?: boolean;
  onValueChange: (value: number) => void;
  value?: number;
  defaultValue?: number;
  displayValue?: string;
  showUpDownArrows?: boolean;
  arrowClickStepSize?: number;
  upArrowDisabled?: boolean;
  downArrowDisabled?: boolean;
  onBlurTransformValue?: (value: number) => number | undefined;
  /**
   * @deprecated This prop will be removed soon. Please validate input outside.
   */
  min?: number;
  /**
   * @deprecated This prop will be removed soon. Please validate input outside.
   */
  max?: number;
};

type UpDownProps = {
  onUpClick: () => void;
  onDownClick: () => void;
  upArrowDisabled: boolean;
  downArrowDisabled: boolean;
};

const UpDownWrapper = styled.div`
  box-shadow: inset 0px 0px 0px 1px ${colors.borderAction};
  border-radius: 0 ${r4} ${r4} 0;
  width: 32px;
  min-width: 32px;
  height: 34px;
  background-color: ${colors.grey200};
  user-select: none;
`;

const UpDownButton = styled.div<{ disabled: boolean }>`
  text-align: center;
  height: 50%;
  color: ${colors.iconAction};
  ${ICON_SELECTOR} {
    font-size: 17px !important;
  }

  ${({ disabled }) =>
    disabled
      ? css`
          color: ${colors.iconActionDisabled};
        `
      : css`
          &:hover {
            background-color: ${colors.grey300};
            cursor: pointer;
            ${ICON_SELECTOR} {
              color: ${colors.iconActionHover};
            }
          }
        `}
`;

const UpDown = ({
  onUpClick,
  onDownClick,
  upArrowDisabled,
  downArrowDisabled,
}: UpDownProps) => (
  <UpDownWrapper>
    <UpDownButton
      disabled={upArrowDisabled}
      onClick={upArrowDisabled ? undefined : onUpClick}
      data-testid="up-arrow"
    >
      <ChevronUpIcon />
    </UpDownButton>
    <UpDownButton
      disabled={downArrowDisabled}
      onClick={downArrowDisabled ? undefined : onDownClick}
      data-testid="down-arrow"
    >
      <ChevronDownIcon />
    </UpDownButton>
  </UpDownWrapper>
);

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      hasError,
      defaultValue,
      value,
      onValueChange,
      allowDecimals,
      allowBlank = true,
      displayValue,
      arrowClickStepSize = 1,
      showUpDownArrows = true,
      upArrowDisabled = false,
      downArrowDisabled = false,
      postfixNode,
      onBlurTransformValue,
      min,
      max,
      ...props
    },
    ref
  ) => {
    const parseFn = useCallback(
      (input: string) =>
        allowDecimals ? parseFloat(input) : parseInt(input, 10),
      [allowDecimals]
    );

    const [textInputValue, setTextInputValue] = useState<string>(
      (Number.isFinite(value) && `${value}`) ||
        (Number.isFinite(defaultValue) && `${defaultValue}`) ||
        (allowBlank ? '' : '0')
    );

    /**
     * This effect syncs the value prop with the internal inputValue,
     * which is necessary when the value changes externally. For example,
     * if a separate reset button sets the value to 0, we need to update
     * the internal state to reflect this change.
     */
    useEffect(() => {
      // Prevents some unnecessary re-renders when values are in sync
      if (parseFn(textInputValue) !== value) {
        const valueToSet =
          (Number.isFinite(value) && `${value}`) || (allowBlank ? '' : '0');

        setTextInputValue(valueToSet);
      }
      // We only want to run this effect when the value prop changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, allowBlank, parseFn]);

    const getArrowClickHandler = (step: number) => () => {
      const parsedValue = parseFn(textInputValue);
      const newValue = (parsedValue || 0) + step;
      if (min !== undefined && newValue < min) return;
      if (max !== undefined && newValue > max) return;

      onValueChange(parseFn(`${newValue}`));
    };

    const handleChange = (value: string) => {
      const parsedValue = parseFn(value);
      setTextInputValue(value);

      const fallbackValue = allowBlank ? '' : '0';
      const newValue = `${
        parsedValue || parsedValue === 0 ? parsedValue : fallbackValue
      }`;

      onValueChange(parseFn(`${newValue}`));
    };

    return (
      <TextInput
        {...props}
        ref={ref}
        hasError={hasError}
        hideErrorIcon
        value={textInputValue}
        onValueChange={number => {
          handleChange(number);
        }}
        displayValue={displayValue}
        postfixNode={
          showUpDownArrows ? (
            <>
              {postfixNode}
              <UpDown
                onUpClick={getArrowClickHandler(arrowClickStepSize)}
                onDownClick={getArrowClickHandler(-arrowClickStepSize)}
                upArrowDisabled={upArrowDisabled}
                downArrowDisabled={downArrowDisabled}
              />
            </>
          ) : (
            postfixNode
          )
        }
        onBlur={() => {
          const parsedValue = parseFn(textInputValue);
          const fallbackValue = allowBlank ? '' : '0';
          let newValue = `${
            parsedValue || parsedValue === 0 ? parsedValue : fallbackValue
          }`;

          if (onBlurTransformValue && isFunction(onBlurTransformValue)) {
            const normalized = onBlurTransformValue(parseFn(newValue));
            newValue = `${normalized ?? fallbackValue}`;
          }

          setTextInputValue(newValue);
          onValueChange(parseFn(newValue));
        }}
      />
    );
  }
);
